import { useMemo } from "react";
import { X } from "lucide-react";
import {
  fmtHM, fmtSignedHM, fmtPct, fmtClock, kpisOf, deriveDay, distribution,
  monthlyRollup, absenceBlocks, dowName, RAG, ragOf,
} from "./adhUtils";

const label = "font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim";
const section = "mk-card p-4";

export default function AdhAgentReport({ data, code, range, onClose }) {
  const t = data.targets;
  const meta = data.agents[code] || {};
  const rows = useMemo(
    () => data.days.filter((r) => r.c === code && r.d >= range.from && r.d <= range.to).sort((a, b) => (a.d < b.d ? -1 : 1)),
    [data, code, range]);
  const k = useMemo(() => kpisOf(rows, t), [rows, t]);
  const dist = useMemo(() => distribution(rows, t), [rows, t]);
  const monthly = useMemo(() => monthlyRollup(rows, t), [rows, t]);
  const blocks = useMemo(() => absenceBlocks(rows), [rows]);
  const rag = useMemo(() => ragOf(k), [k]);
  const short = useMemo(
    () => rows.map((r) => ({ r, kd: deriveDay(r, t) })).filter((x) => x.kd.valid && x.r.wm < 480).sort((a, b) => a.r.wm - b.r.wm).slice(0, 15),
    [rows, t]);
  const corrupt = useMemo(() => rows.filter((r) => r.f), [rows]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="adh-agent-drawer">
      <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-thborder/10 bg-appbg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-start justify-between">
          <div>
            <p className={label}>Discipline & Hygiene Report · {range.from} → {range.to}</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-thstrong">{meta.name || code}</h2>
            <p className="mt-1 text-xs text-thmute">
              {code}{meta.cisco ? ` · Cisco ${meta.cisco}` : ""} · {meta.site}{meta.tl ? ` · TL ${meta.tl}` : ""}{meta.sub ? ` · ${meta.sub}` : ""}
            </p>
          </div>
          <button data-testid="adh-drawer-close" onClick={onClose} className="rounded-md p-2 text-thmute hover:bg-thhover/10 hover:text-thstrong"><X className="h-5 w-5" /></button>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[
            ["met", `Days ≥ ${t.hours}h`, fmtPct(k.pctMet), `${k.validDays} valid days`],
            ["avg", "Avg in office", fmtHM(k.avgMin), `median ${fmtHM(k.medMin)}`],
            ["net", `Net vs ${t.hours}h`, fmtSignedHM(k.deficit), "surplus / deficit"],
            ["late", "Late beyond grace", `${k.lateN} (${fmtPct(k.latePct)})`, `median in ${fmtClock(k.medIn)}`],
            ["early", "Early departures", `${k.earlyN} (${fmtPct(k.earlyPct)})`, `median out ${fmtClock(k.medOut)}`],
            ["att", "Mon–Fri attendance", fmtPct(k.monFriPct), `${k.present} present · ${k.absent} absent`],
          ].map(([id, l, v, sub]) => (
            <div key={id} className={section} data-testid={`adh-report-${id}`}>
              <p className={label}>{l}</p>
              <p className="mt-1.5 font-heading text-xl font-bold text-thstrong">{v}</p>
              <p className="font-mono-hud mt-1 text-[9px] uppercase tracking-[0.12em] text-thdim">{sub}</p>
            </div>
          ))}
        </div>

        <div className={`${section} mb-5`} data-testid="adh-report-rag">
          <p className={label}>Assessment</p>
          <div className="mt-3 space-y-2">
            {rag.map((r) => (
              <div key={r.dim} className="flex items-center gap-3">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: RAG[r.rag] }} />
                <span className="w-44 shrink-0 text-xs font-medium text-thstrong">{r.dim}</span>
                <span className="text-xs text-thmute">{r.evidence}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`${section} mb-5`} data-testid="adh-report-distribution">
          <p className={label}>Hours distribution · valid days</p>
          <div className="mt-3 space-y-2">
            {dist.filter((b) => b.n).map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="w-40 shrink-0 text-xs text-thbody">{b.label}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-thhover/10">
                  <div className="h-full rounded-full bg-[#14B8A6]" style={{ width: `${b.pct}%` }} />
                </div>
                <span className="w-14 shrink-0 text-right font-mono-hud text-xs text-thbody">{b.n}</span>
              </div>
            ))}
          </div>
        </div>

        {blocks.length > 0 && (
          <div className={`${section} mb-5`} data-testid="adh-report-blocks">
            <p className={label}>Absence blocks (3+ consecutive days)</p>
            <div className="mt-3 space-y-1.5">
              {blocks.map((b) => (
                <p key={b.from} className="text-xs text-thmute">
                  <span className="font-mono-hud text-thbody">{b.from} → {b.to}</span> · {b.len} days ({b.weekdays} weekdays)
                </p>
              ))}
            </div>
          </div>
        )}

        <div className={`${section} mb-5 overflow-x-auto p-0`} data-testid="adh-report-monthly">
          <p className={`${label} px-4 pt-3`}>Month by month</p>
          <table className="mt-2 w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              {["Month", "P", "A", "Med in", "Med out", "Med hrs", `≥${t.hours}h`, "Late %"].map((h, i) => (
                <th key={h} className={`font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.1em] text-thdim ${i ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {monthly.map((m) => (
                <tr key={m.m} className="border-b border-thborder/5">
                  <td className="px-3 py-2 text-xs font-medium text-thstrong">{m.m}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{m.present}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{m.absent}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtClock(m.medIn)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtClock(m.medOut)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtHM(m.medMin)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(m.pctMet)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(m.latePct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`${section} mb-5 overflow-x-auto p-0`} data-testid="adh-report-exceptions">
          <p className={`${label} px-4 pt-3`}>Exception log · shortest valid days (under 8h)</p>
          <table className="mt-2 w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              {["Date", "Day", "In", "Out", "Hours", `Vs ${t.hours}h`].map((h, i) => (
                <th key={h} className={`font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.1em] text-thdim ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {short.map(({ r, kd }) => (
                <tr key={r.d} className="border-b border-thborder/5">
                  <td className="px-3 py-2 font-mono-hud text-xs text-thbody">{r.d}</td>
                  <td className="px-3 py-2 text-xs text-thmute">{dowName(r.d)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtClock(r.ti)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtClock(r.to)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtHM(r.wm)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-[#F43F5E]">{fmtSignedHM(kd.deficit)}</td>
                </tr>
              ))}
              {!short.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-thdim">No valid days under 8h in this range.</td></tr>}
            </tbody>
          </table>
        </div>

        {corrupt.length > 0 && (
          <div className={section} data-testid="adh-report-corrupt">
            <p className={label}>Data integrity exceptions · excluded from duration stats</p>
            <div className="mt-3 space-y-1.5">
              {corrupt.slice(0, 12).map((r) => (
                <p key={r.d + r.f} className="text-xs text-thmute">
                  <span className="font-mono-hud text-thbody">{r.d}</span> · {dowName(r.d)} ·{" "}
                  {r.f === "rev" ? `reversed punches (in ${fmtClock(r.ti)} / out ${fmtClock(r.to)})` : r.f === "mo" ? `missing punch-out (in ${fmtClock(r.ti)})` : `missing punch-in (out ${fmtClock(r.to)})`}
                </p>
              ))}
              {corrupt.length > 12 && <p className="text-xs text-thdim">…and {corrupt.length - 12} more</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
